import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { CombinedState } from 'reducers';
import { fetchAnnotationsAsync, saveAnnotationsAsync } from 'actions/annotation-actions';
import { isReferenceJob } from 'utils/reference-job';
import { useTaskModes } from '../references/use-task-modes';
import type { ReferencePhoto } from '../references/references-api';
import { StepsProvider } from './steps-context';

const NO_PHOTOS: ReferencePhoto[] = [];

export default function StepsProviderContainer({ children }: { children: React.ReactNode }): JSX.Element {
    const dispatch = useDispatch();
    const job = useSelector((state: CombinedState) => state.annotation.job.instance);
    const frame = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    // a reference job only knows its task from the route: /tasks/:tid/references/:rid/jobs/:jid
    const { tid } = useParams<{ tid?: string }>();
    const routeTaskId = Number(tid);
    const inTask = Number.isInteger(routeTaskId) && routeTaskId > 0;
    const reference = !!job && isReferenceJob(job);
    const modes = useTaskModes(inTask && reference ? routeTaskId : 0);

    // serialises the whole collection: called on demand only, never per render
    const hasUnsavedChanges = useCallback(() => !!job && job.annotations.hasUnsavedChanges(), [job]);
    const save = useCallback(async () => {
        await dispatch(saveAnnotationsAsync() as any);
        if (job && job.annotations.hasUnsavedChanges()) throw new Error('Your changes could not be saved');
    }, [dispatch, job]);
    const reloadAnnotations = useCallback(async () => {
        await dispatch(fetchAnnotationsAsync(true) as any);
    }, [dispatch]);

    if (!job) return children as JSX.Element;
    if (reference) {
        // the legacy reference page has no task: no steps there
        if (!inTask) return children as JSX.Element;
        // rendered before the photos load too: swapping the tree later would remount the canvas
        const photos = modes?.photos ?? NO_PHOTOS;
        const current = photos.find((photo) => photo.jobId === job.id && photo.frame === frame);
        return (
            <StepsProvider
                taskId={routeTaskId}
                projectId={job.projectId ?? null}
                scope='references'
                photos={photos}
                currentFrame={current ? current.index : -1}
                hasUnsavedChanges={hasUnsavedChanges}
                save={save}
                reloadAnnotations={reloadAnnotations}
            >
                {children}
            </StepsProvider>
        );
    }
    const taskId = 'taskId' in job ? job.taskId : null;
    if (!taskId) return children as JSX.Element;
    return (
        <StepsProvider
            taskId={taskId}
            projectId={job.projectId ?? null}
            currentFrame={frame}
            hasUnsavedChanges={hasUnsavedChanges}
            save={save}
            reloadAnnotations={reloadAnnotations}
        >
            {children}
        </StepsProvider>
    );
}
